import { useGetValuation, useGetValuations } from 'ClientPortal/src/api/Queries/valuations'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import { useDeviationAnalysisComponent } from '../../AssetDeviationAnalysis/AssetDeviationAnalysisComponent.data'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import { useGetPlausibility, usePatchPlausibility } from 'ClientPortal/src/api/Queries/plausibility'
import { useGetOrder, usePatchOrder } from 'ClientPortal/src/api/Queries/orders'
import { Annotation, AnnotationMessage } from '@einwert/valuation-api-client'
import { Operation } from 'fast-json-patch'
import { useGetAppraiserUsers, useGetUsers } from 'ClientPortal/src/api/Queries/users'
import { sortBy } from 'lodash'
import dayjs from 'dayjs'
import { useIamContext } from 'ClientPortal/src/context/IAmContext'
import { useDeviationAnalysis } from '../../useDeviationAnalysis'
import { UUID } from 'ComponentLibrary/src/common/uuid'

export function useValuationPlausibility() {
    const { valuationId } = useTypedParams(['valuationId'])
    const { valuation, isValuationPending } = useGetValuation({ valuationId, throwOnError: throwOnForbiddenApiError })

    const { asset, assetId, valuationOptions, isAssetPending } = useDeviationAnalysisComponent()

    const { valuations, isValuationsPending } = useGetValuations({ assetId, includeDrafts: true })

    const draftsWithSameOrder = valuations.filter(
        (valuationItem) => valuationItem.order_uuid === valuation?.order_uuid && valuationItem.is_draft
    )
    const draftOptions = sortBy(draftsWithSameOrder, (option) => dayjs(option.created_on).valueOf()).toReversed()

    const { user: currentUser, isUserPending } = useIamContext()
    const { users } = useGetUsers({ orderId: valuation?.order_uuid, enabled: !!valuation?.order_uuid })
    const { appraiserUsers, isAppraiserUsersPending } = useGetAppraiserUsers({
        orderId: valuation?.order_uuid,
        enabled: !!valuation?.order_uuid,
    })
    const { order, isOrderInitialLoading } = useGetOrder({ orderId: valuation?.order_uuid })
    const { patchOrder: patchAssignedObservers, isPatchingOrder: isEditObserversPending } = usePatchOrder({
        orderId: valuation?.order_uuid,
    })

    const { plausibility, isPlausibilityPending } = useGetPlausibility({ valuationId })
    const { patchPlausibility: addAnnotationMutation, isPatchingPlausibility: isAddAnnotationPending } = usePatchPlausibility({
        valuationId,
    })
    const { patchPlausibility: editAnnotationMutation, isPatchingPlausibility: isEditAnnotationPending } = usePatchPlausibility({
        valuationId,
    })
    const { patchPlausibility: deleteAnnotationMutation, isPatchingPlausibility: isDeleteAnnotationPending } = usePatchPlausibility({
        valuationId,
    })
    const { patchPlausibility: editPlausibilityMutation, isPatchingPlausibility: isEditPlausibilityPending } = usePatchPlausibility({
        valuationId,
    })

    const annotations = plausibility?.annotations || []
    const participantIds = plausibility?.participant_ids || []

    const addAnnotation = (annotation: Annotation, onSuccess?: () => void) => {
        addAnnotationMutation(
            [
                {
                    op: 'add',
                    path: `/annotations/-`,
                    value: annotation,
                },
            ],
            { onSuccess }
        )
    }

    const addReply = (reply: AnnotationMessage, annotationId?: string, onSuccess?: () => void) => {
        const annotationIndex = annotations.findIndex((a) => a.uuid === annotationId)
        addAnnotationMutation(
            [
                {
                    op: 'add',
                    path: `/annotations/${annotationIndex}/replies/-`,
                    value: reply,
                },
            ],
            { onSuccess }
        )
    }

    const deleteAnnotation = (annotationId?: string, cascade?: boolean, onSuccess?: () => void) => {
        const annotationIndex = annotations.findIndex((annotation) => annotation.uuid === annotationId)
        const annotation = annotations[annotationIndex]

        const operations: Operation[] = []

        if (!annotation.is_deleted && !annotation.is_read_only) {
            operations.push({
                op: 'replace',
                path: `/annotations/${annotationIndex}/is_deleted`,
                value: true,
            })
        }

        if (cascade && annotation.replies) {
            annotation.replies.forEach((reply, replyIndex) => {
                if (!reply.is_deleted && !reply.is_read_only) {
                    operations.push({
                        op: 'replace',
                        path: `/annotations/${annotationIndex}/replies/${replyIndex}/is_deleted`,
                        value: true,
                    })
                }
            })
        }

        if (operations.length > 0) {
            deleteAnnotationMutation(operations, { onSuccess })
        }
    }

    const deleteReply = (annotationId?: string, replyId?: string, onSuccess?: () => void) => {
        const annotationIndex = annotations.findIndex((a) => a.uuid === annotationId)
        const replyIndex = annotations[annotationIndex].replies?.findIndex((r) => r.uuid === replyId)
        deleteAnnotationMutation(
            [
                {
                    op: 'replace',
                    path: `/annotations/${annotationIndex}/replies/${replyIndex}/is_deleted`,
                    value: true,
                },
            ],
            { onSuccess }
        )
    }

    const editAnnotation = (updatedText: string, annotationId?: string, onSuccess?: () => void) => {
        const annotationIndex = annotations.findIndex((a) => a.uuid === annotationId)
        editAnnotationMutation(
            [
                {
                    op: 'replace',
                    path: `/annotations/${annotationIndex}/text`,
                    value: updatedText,
                },
            ],
            { onSuccess }
        )
    }

    const editReply = (updatedText: string, annotationId?: string, replyId?: string, onSuccess?: () => void) => {
        const annotationIndex = annotations.findIndex((a) => a.uuid === annotationId)
        const replyIndex = annotations[annotationIndex].replies?.findIndex((r) => r.uuid === replyId)
        editAnnotationMutation(
            [
                {
                    op: 'replace',
                    path: `/annotations/${annotationIndex}/replies/${replyIndex}/text`,
                    value: updatedText,
                },
            ],
            { onSuccess }
        )
    }

    const setParticipantIds = (ids: number[]) => {
        editPlausibilityMutation([
            {
                op: 'replace',
                path: '/participant_ids',
                value: ids,
            },
        ])
    }

    const observerUUIDs = order?.observers ?? []

    const setObserverUUIDs = (assignedObserverIds: UUID[]) => {
        const addOperations = assignedObserverIds
            .filter((observerId) => !observerUUIDs.includes(observerId))
            .map((observerId) => ({
                op: 'add' as const,
                path: '/observers/-',
                value: observerId,
            }))

        const removeOperations = observerUUIDs
            .map((observerId, index) => ({ observerId, index }))
            .filter(({ observerId }) => !assignedObserverIds.includes(observerId))
            .sort((a, b) => b.index - a.index)
            .map(({ index }) => ({
                op: 'remove' as const,
                path: `/observers/${index}`,
            }))

        const operations = [...removeOperations, ...addOperations]

        if (operations.length > 0) {
            patchAssignedObservers(operations)
        }
    }

    const addObserver = (observerUUID: UUID) => {
        patchAssignedObservers([
            {
                op: 'add' as const,
                path: '/observers/-',
                value: observerUUID,
            },
        ])
    }

    const removeObserver = (index: number) => {
        patchAssignedObservers([
            {
                op: 'remove' as const,
                path: `/observers/${index}`,
            },
        ])
    }

    const setIsHandOver = (newHandOver: boolean, idsToUpdate?: number[]) => {
        const operations: Operation[] = [
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_handed_over',
                value: newHandOver,
            },
        ]

        editPlausibilityMutation(operations)
    }

    const setIsApproved = (newApproval: boolean, idsToUpdate?: number[]) => {
        const operations: Operation[] = [
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_approved',
                value: newApproval,
            },
        ]

        editPlausibilityMutation(operations)
    }

    const setIsInternallyApproved = (newApproval: boolean, idsToUpdate?: number[]) => {
        const operations: Operation[] = [
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_internally_approved',
                value: newApproval,
            },
        ]

        editPlausibilityMutation(operations)
    }

    const { deviationAnalysis, isDeviationAnalysisPending, baseValuation, comparisonValuation } = useDeviationAnalysis({
        valuationOptions,
        selectedValuation: valuation,
        lockedBaseValuation: true,
    })
    const observerUsers = observerUUIDs
        .map((id) => users.find((user) => user.uuid === id))
        .filter((user): user is (typeof users)[number] => !!user)

    return {
        valuation,
        valuationId,
        isValuationPlausibilityPending:
            isValuationPending ||
            isAssetPending ||
            isPlausibilityPending ||
            isValuationsPending ||
            isUserPending ||
            isOrderInitialLoading ||
            isDeviationAnalysisPending,
        deviationAnalysis,
        baseValuation,
        comparisonValuation,
        asset,
        plausibility,
        annotations: annotations.filter((annotation) => !annotation.is_deleted || annotation.replies?.some((reply) => !reply.is_deleted)),
        addAnnotation,
        addReply,
        deleteAnnotation,
        deleteReply,
        editAnnotation,
        editReply,
        isAddAnnotationPending,
        isEditAnnotationPending,
        isDeleteAnnotationPending,
        currentUser,
        users,
        draftOptions,
        participantIds,
        setParticipantIds,
        observerUUIDs,
        setObserverUUIDs,
        addObserver,
        removeObserver,
        isEditPlausibilityPending,
        isEditObserversPending,
        setIsHandOver,
        setIsApproved,
        setIsInternallyApproved,
        participantUsers: plausibility?.participant_users ?? [],
        observerUsers,
        appraiserUsers,
        isAppraiserUsersPending,
        orderAssignedCustomerUsers: order?.assigned_customer_users ?? [],
        orderAssignedAppraiserId: order?.appraiser_assignment?.main,
    }
}
